import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, Instagram, Facebook, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { INSTAGRAM_URL, TIKTOK_URL, FACEBOOK_URL, YOUTUBE_URL, X_URL, TikTokIcon, XIcon } from "@/components/Header";

const SOCIALS = [
  { label: "Instagram", href: INSTAGRAM_URL, Icon: Instagram, testId: "stay-social-instagram" },
  { label: "TikTok", href: TIKTOK_URL, Icon: TikTokIcon, testId: "stay-social-tiktok" },
  { label: "Facebook", href: FACEBOOK_URL, Icon: Facebook, testId: "stay-social-facebook" },
  { label: "YouTube", href: YOUTUBE_URL, Icon: Youtube, testId: "stay-social-youtube" },
  { label: "X", href: X_URL, Icon: XIcon, testId: "stay-social-x" },
];

export function StayPoolside() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { pathname } = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/subscribe", { email, source: pathname });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please enter a valid email address");
    }
    setBusy(false);
  };

  return (
    <section className="bg-pool-light/40 border-t border-pool/15" data-testid="stay-poolside">
      <div className="container-site py-10 sm:py-12 text-center max-w-2xl mx-auto">
        <Reveal>
          <h2 className="font-display font-black tracking-tight leading-[1.02] text-2xl sm:text-3xl lg:text-4xl text-ink" data-testid="stay-poolside-headline">
            <span className="text-pool-deep">STAY</span> <span className="text-pool">POOLSIDE.</span>
          </h2>
          <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed max-w-xl mx-auto" data-testid="stay-poolside-copy">
            New episodes. Special guests. Events. Recipes. Poolside moments. Straight to your inbox.
          </p>
          {done ? (
            <p
              className="mt-8 inline-flex items-center gap-2.5 bg-white border border-pool/30 text-pool-deep font-semibold text-sm px-7 py-4 rounded-full"
              data-testid="stay-poolside-confirmation"
            >
              <Check className="h-4 w-4 text-pool" /> You&rsquo;re Poolside. We&rsquo;ll keep you in the loop.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="stay-poolside-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                aria-label="Email address"
                className="flex-1 bg-white border border-slate-200 text-ink placeholder:text-slate-400 text-sm px-5 py-4 rounded-full focus:outline-none focus:ring-2 focus:ring-pool/50 focus:border-pool"
                data-testid="stay-poolside-email"
              />
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center justify-center gap-2.5 bg-pool hover:bg-pool-dark disabled:opacity-60 text-white font-bold text-xs tracking-[0.15em] uppercase px-7 py-4 rounded-full transition-colors"
                data-testid="stay-poolside-submit"
              >
                {busy ? "Joining…" : <>Join the List <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          )}
          <p className="mt-5 text-slate-400 text-xs" data-testid="stay-poolside-note">
            Good food. Good vibes. Good updates. No spam. ·{" "}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-pool transition-colors" data-testid="stay-poolside-privacy">
              Privacy Policy
            </Link>
          </p>
          <div className="mt-6 flex items-center justify-center gap-5" data-testid="stay-poolside-socials">
            {SOCIALS.map(({ label, href, Icon, testId }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={`${label} Poolside Cooking`}
                className="text-pool-deep/50 hover:text-pool transition-colors"
                data-testid={testId}
              >
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
